import React from 'react';
import { Box } from '@chakra-ui/react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { useTranslations } from '../../../translations/useTranslations';
import { sanitizeErrors } from '../../../utils/errors';
import { useCommonOptionsStore } from '../../../store';
import { useNextActionHandler } from '../../../handlers/nextActionHandler';

import type { CommonPaymentElementPropTypes } from '../../../propTypes';
import type {
  FormInputsType,
  NextAction,
  PaymentElementResponse,
  PaymentMethodWithSubmitProps,
} from '../../../@types';

type BankFormPropTypes = CommonPaymentElementPropTypes & {
  pm?: string;
};

type BankFormProps = PaymentMethodWithSubmitProps<BankFormPropTypes>;

const layoutProps: Record<string, any> = { gap: 6, columnGap: 4 };

export const BankForm = React.forwardRef(
  (
    { onLoadStart, onLoadEnd, onError, onSubmit, pm }: BankFormProps,
    forwardRef: React.Ref<{
      submit: () => void;
      validate: () => Promise<boolean>;
    }>,
  ) => {
    const lang = useCommonOptionsStore(state => state.lang);
    const [t] = useTranslations(lang);
    const formMethods = useForm<FormInputsType>({ mode: 'onChange' });
    const { handleSubmit, formState } = formMethods;
    const { errors, isLoading, isSubmitting } = formState;
    const nextActionHandler = useNextActionHandler();

    React.useEffect(() => {
      if (onLoadStart && isLoading) onLoadStart();
      if (onLoadEnd && isLoading) onLoadEnd();
    }, [isLoading, onLoadStart, onLoadEnd]);

    React.useEffect(() => {
      if (onLoadStart && isSubmitting) onLoadStart();
      if (onLoadEnd && isSubmitting) onLoadEnd();
    }, [isSubmitting, onLoadStart, onLoadEnd]);

    const submitHandler: SubmitHandler<FormInputsType> = async (
      formData: any,
    ) => {
      const data = { ...formData };

      if (onSubmit) {
        const responseData: PaymentElementResponse = {
          ...data,
          paymentMethod: pm,
        };

        const nextAction: NextAction = await onSubmit({ ...responseData });
        await nextActionHandler(nextAction);
      }
    };

    const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
      if (onError) onError(sanitizeErrors(errors));
    };

    React.useImperativeHandle(forwardRef, () => ({
      submit() {
        handleSubmit(submitHandler, submitErrorHandler)();
      },
      async validate() {
        return true;
      },
    }));

    return (
      <>
        <FormProvider {...formMethods}>
          <Box paddingTop={(layoutProps as any).gap}>
            {t('bankTransferMessage')}
          </Box>
        </FormProvider>
      </>
    );
  },
);
